"use client";

import React from "react";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { CircleCheck as CheckCircle2, ArrowRight } from 'lucide-react';
export default function OutdoorLivingFaq() {
  return <section id="outdoorlivingfaq" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-start">
          {/* Left Column: Heading, Context, and Visual Imagery */}
          <div className="lg:col-span-5 flex flex-col gap-8 lg:sticky lg:top-24">
            <div className="flex flex-col gap-4">
              <span className="text-xs uppercase tracking-widest text-primary font-mono font-semibold">
                Brookhaven Living Guide
              </span>
              <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground leading-[1.15]">
                Clear answers for your Brookhaven outdoor renovation.
              </h2>
              <p className="text-muted-foreground text-base leading-relaxed">
                From City Hall setbacks and DeKalb County tree preservation laws
                to humidity-tested materials, here is how we engineer durable,
                code-compliant outdoor living spaces.
              </p>
            </div>

            {/* Curated Images */}
            <div className="flex flex-col gap-4">
              <div className="relative overflow-hidden rounded-lg border border-border shadow-md aspect-[4/3] bg-muted">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_covered_patio_outdoor_living.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_covered_patio_outdoor_living.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_covered_patio_outdoor_living.png-wvc-sizes" alt="Custom cedar pavilion with stone fireplace and integrated outdoor kitchen in Brookhaven" className="w-full h-full object-cover" loading="lazy" decoding="async" />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="relative overflow-hidden rounded-lg border border-border shadow-xs aspect-[4/3] bg-muted">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_kitchen_bluestone_patio.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_kitchen_bluestone_patio.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1821096/img/brookhaven_outdoor_kitchen_bluestone_patio.png-wvc-sizes" alt="Natural bluestone patio paving surrounding outdoor prep island and grill" className="w-full h-full object-cover" loading="lazy" decoding="async" />
                </div>
                <div className="bg-card border border-border rounded-lg p-5 flex flex-col justify-between shadow-xs">
                  <div>
                    <p className="text-xs font-mono uppercase tracking-wider text-muted-foreground mb-1">
                      Local Experience
                    </p>
                    <p className="text-sm font-medium text-foreground">
                      Direct permitting coordination with City of Brookhaven
                      Community Development.
                    </p>
                  </div>
                  <div className="pt-2">
                    <Button variant="link" className="p-0 h-auto text-primary group" asChild>
                      <Link to="/ContactRequestAnEstimatePage">
                        <span>Discuss your site</span>
                        <ArrowRight className="size-3.5 transition-transform group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Right Column: Accordion Questions */}
          <div className="lg:col-span-7">
            <Accordion type="single" collapsible defaultValue="item-1" className="w-full space-y-4">
              {/* Question 1: Permits and Zoning */}
              <AccordionItem value="item-1" className="bg-card border border-border rounded-lg px-6 py-2 shadow-xs">
                <AccordionTrigger className="text-base sm:text-lg font-serif font-medium text-foreground hover:no-underline hover:text-primary transition-colors text-left py-4">
                  Do permanent outdoor living structures require permits in
                  Brookhaven?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed text-sm sm:text-base space-y-4 pt-1 pb-5">
                  <p>
                    Yes. Any covered pavilion, roof extension, screened porch,
                    deck elevated greater than thirty inches, or masonry
                    fireplace requires a building permit through the City of
                    Brookhaven Community Development Department.
                  </p>
                  <p>
                    Brookhaven strictly enforces property setback lines and total
                    lot impervious surface ratios (typically capped between 35%
                    and 50% depending on your specific zoning district).
                    Additionally, any site grading or footing excavation near
                    mature trees triggers Brookhaven's Tree Protection
                    Ordinance, requiring specific critical root zone protection
                    plans.
                  </p>
                  <div className="bg-muted/60 rounded-md p-4 border border-border/80">
                    <p className="text-xs font-semibold uppercase tracking-wider text-foreground mb-2">
                      What we manage on your behalf:
                    </p>
                    <ul className="space-y-1.5 text-xs sm:text-sm text-foreground/90">
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>Certified boundary and topographic surveys</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>Impervious surface calculations and lot coverage audits</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                        <span>Arborist consultations and tree protection fencing plans</span>
                      </li>
                    </ul>
                  </div>
                </AccordionContent>
              </AccordionItem>

              {/* Question 2: Durability of Materials */}
              <AccordionItem value="item-2" className="bg-card border border-border rounded-lg px-6 py-2 shadow-xs">
                <AccordionTrigger className="text-base sm:text-lg font-serif font-medium text-foreground hover:no-underline hover:text-primary transition-colors text-left py-4">
                  Which materials hold up best in Georgia's humid, rainy climate?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed text-sm sm:text-base space-y-3 pt-1 pb-5">
                  <p>
                    Atlanta's humid summers, heavy rain downpours, and freeze-thaw
                    winter cycles quickly deteriorate standard pine and untreated
                    hardware. We engineer outdoor spaces using specific
                    resilient materials:
                  </p>
                  <ul className="list-disc pl-5 space-y-2">
                    <li>
                      <strong className="text-foreground font-medium">
                        Cabinetry & Appliances:
                      </strong>{" "}
                      Marine-grade high-density polyethylene (HDPE) or 304/316
                      marine-grade stainless steel that resists oxidation, mildew,
                      and sun fading.
                    </li>
                    <li>
                      <strong className="text-foreground font-medium">
                        Patio Surfaces:
                      </strong>{" "}
                      Wet-cast architectural pavers, Pennsylvania thermal
                      bluestone, or dense porcelain exterior slabs that prevent
                      moisture absorption and resisting slip hazards.
                    </li>
                    <li>
                      <strong className="text-foreground font-medium">
                        Framing & Ceilings:
                      </strong>{" "}
                      Western red cedar, thermally modified ash, and PVC beadboard
                      soffits treated to repel moisture, mold, and wood-boring
                      insects.
                    </li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              {/* Question 3: Utility Runs */}
              <AccordionItem value="item-3" className="bg-card border border-border rounded-lg px-6 py-2 shadow-xs">
                <AccordionTrigger className="text-base sm:text-lg font-serif font-medium text-foreground hover:no-underline hover:text-primary transition-colors text-left py-4">
                  How are utility runs handled for an outdoor kitchen?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed text-sm sm:text-base space-y-3 pt-1 pb-5">
                  <p>
                    Outdoor kitchens require dedicated infrastructure channeled
                    below ground before any patio base or footing is poured:
                  </p>
                  <ul className="space-y-2 text-sm sm:text-base">
                    <li className="flex items-start gap-2.5">
                      <span className="font-mono text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded shrink-0 mt-0.5">
                        GAS
                      </span>
                      <span>
                        Underground continuous poly gas lines run from your main
                        meter, sized properly for high-BTU grills, pizza ovens,
                        and fire features.
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="font-mono text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded shrink-0 mt-0.5">
                        ELEC
                      </span>
                      <span>
                        Dedicated GFCI circuits pulled through rigid schedule 40
                        conduit to handle refrigerators, draft taps, warming
                        drawers, and overhead heaters.
                      </span>
                    </li>
                    <li className="flex items-start gap-2.5">
                      <span className="font-mono text-xs font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded shrink-0 mt-0.5">
                        PLUMB
                      </span>
                      <span>
                        Insulated PEX water lines with interior shutoff and drain
                        valves inside your crawlspace or basement, paired with
                        gravity drain lines tied directly into your home's waste
                        system.
                      </span>
                    </li>
                  </ul>
                </AccordionContent>
              </AccordionItem>

              {/* Question 4: Maintenance and Winterization */}
              <AccordionItem value="item-4" className="bg-card border border-border rounded-lg px-6 py-2 shadow-xs">
                <AccordionTrigger className="text-base sm:text-lg font-serif font-medium text-foreground hover:no-underline hover:text-primary transition-colors text-left py-4">
                  What ongoing maintenance and winterization is required?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed text-sm sm:text-base space-y-3 pt-1 pb-5">
                  <p>
                    While our selected materials minimize upkeep, Georgia winter
                    temperatures occasionally drop into the teens. We build in
                    accessible shutoff ports so winterization takes under 20
                    minutes:
                  </p>
                  <ol className="list-decimal pl-5 space-y-2">
                    <li>
                      <strong className="text-foreground font-medium">
                        Line Blowout:
                      </strong>{" "}
                      Isolate the dedicated indoor supply valve and open exterior
                      faucets to blow out residual water with low-pressure air.
                    </li>
                    <li>
                      <strong className="text-foreground font-medium">
                        Stone & Grout:
                      </strong>{" "}
                      Natural flagstone and pavers benefit from a breathable
                      penetrating sealer reapplied every three to four years to
                      prevent moss and oil stains.
                    </li>
                    <li>
                      <strong className="text-foreground font-medium">
                        Outdoor Refrigeration:
                      </strong>{" "}
                      Unplug and wipe down beverage units when temperatures dip
                      below freezing for extended stretches.
                    </li>
                  </ol>
                </AccordionContent>
              </AccordionItem>

              {/* Question 5: Project Timeline */}
              <AccordionItem value="item-5" className="bg-card border border-border rounded-lg px-6 py-2 shadow-xs">
                <AccordionTrigger className="text-base sm:text-lg font-serif font-medium text-foreground hover:no-underline hover:text-primary transition-colors text-left py-4">
                  What is the typical timeline from design to final inspection?
                </AccordionTrigger>
                <AccordionContent className="text-muted-foreground leading-relaxed text-sm sm:text-base space-y-3 pt-1 pb-5">
                  <p>
                    A comprehensive outdoor renovation typically takes between 8
                    and 14 weeks from initial concept through punch list:
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-1">
                    <div className="border border-border/70 rounded-md p-3.5 bg-background">
                      <span className="font-mono text-xs text-primary font-medium block mb-1">
                        Weeks 1–4
                      </span>
                      <p className="text-xs text-foreground font-medium mb-1">
                        Design & Municipal Permitting
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Site surveys, structural drawings, and City of Brookhaven
                        review.
                      </p>
                    </div>
                    <div className="border border-border/70 rounded-md p-3.5 bg-background">
                      <span className="font-mono text-xs text-primary font-medium block mb-1">
                        Weeks 5–7
                      </span>
                      <p className="text-xs text-foreground font-medium mb-1">
                        Site Work & Utilities
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Trenching, deep concrete footings, and underground rough-ins.
                      </p>
                    </div>
                    <div className="border border-border/70 rounded-md p-3.5 bg-background">
                      <span className="font-mono text-xs text-primary font-medium block mb-1">
                        Weeks 8–11
                      </span>
                      <p className="text-xs text-foreground font-medium mb-1">
                        Structure & Hardscapes
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Timber framing, roof tie-in, paver bases, and stone veneer.
                      </p>
                    </div>
                    <div className="border border-border/70 rounded-md p-3.5 bg-background">
                      <span className="font-mono text-xs text-primary font-medium block mb-1">
                        Weeks 12–14
                      </span>
                      <p className="text-xs text-foreground font-medium mb-1">
                        Appliances & Finishes
                      </p>
                      <p className="text-xs text-muted-foreground">
                        Countertop templating, fixture install, and final city
                        inspections.
                      </p>
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
      </div>
    </section>;
}